import { Box, Button, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";

const FinanceQuerySection = ({ sra, storedUser }) => {
  const [queries, setQueries] = useState([]);
  const [question, setQuestion] = useState("");
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [answerLoading, setAnswerLoading] = useState({});
  const [error, setError] = useState("");

  const role = storedUser?.userRole?.[0];

  const isBuyer = role === "BUYER";
  const isFinanceProcessor = role === "FINANCE_PROCESSOR";

  const getQueries = async () => {
    if (!sra?.invoiceId) return;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/invoice-approval/question?invoiceId=${sra.invoiceId}`,
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );

      setQueries(res.data || []);
    } catch (error) {
      console.log(error);
      setError(
        error.response?.data?.Exception || "Failed to load finance queries.",
      );
    }
  };

  useEffect(() => {
    if (sra?.invoiceId && (isBuyer || isFinanceProcessor)) {
      getQueries();
    }
  }, [sra?.invoiceId]);

  const handleRaiseQuery = async () => {
    if (!question.trim()) {
      setError("Please enter a question.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/invoice-approval/question`,
        {
          invoiceId: sra.invoiceId,
          username: storedUser.username,
          question: question.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );

      setQuestion("");
      await getQueries();
    } catch (error) {
      console.log(error);
      setError(error.response?.data?.Exception || "Failed to raise query.");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerQuery = async (queryId) => {
    const currentAnswer = answers[queryId];

    if (!currentAnswer?.trim()) {
      setError("Please enter an answer.");
      return;
    }

    setAnswerLoading((prev) => ({
      ...prev,
      [queryId]: true,
    }));

    setError("");

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/invoice-approval/question`,
        {
          questionId: queryId,
          username: storedUser.username,
          answer: currentAnswer.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );

      setAnswers((prev) => ({
        ...prev,
        [queryId]: "",
      }));

      await getQueries();
    } catch (error) {
      console.log(error);
      setError(error.response?.data?.Exception || "Failed to answer query.");
    } finally {
      setAnswerLoading((prev) => ({
        ...prev,
        [queryId]: false,
      }));
    }
  };

  if (!isBuyer && !isFinanceProcessor) {
    return null;
  }

  return (
    <Box className="dashboard-container">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mb: 2,
        }}
      >
        Finance Query
      </Typography>

      {error && (
        <Box
          sx={{
            mb: 2,
            padding: "10px 12px",
            borderRadius: "8px",
            backgroundColor: "#fff3f3",
            border: "1px solid #ffcdd2",
            color: "#d32f2f",
            fontSize: "13px",
          }}
        >
          {error}
        </Box>
      )}

      {queries.length === 0 ? (
        <Typography
          sx={{
            fontSize: "13px",
            color: "#777",
            mb: 2,
          }}
        >
          No finance queries have been raised.
        </Typography>
      ) : (
        <Box>
          {queries.map((query) => (
            <Box
              key={query.questionId}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "10px",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {/* Question */}
              <Box
                sx={{
                  padding: "14px 16px",
                  backgroundColor: "#f7fbff",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#555",
                    mb: 0.5,
                  }}
                >
                  Finance Processor
                </Typography>

                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#222",
                  }}
                >
                  {query.question}
                </Typography>

                {query.createdDate && (
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#888",
                      mt: 0.8,
                    }}
                  >
                    {new Date(query.createdDate).toLocaleString()}
                  </Typography>
                )}
              </Box>

              {/* Answer */}
              {query.answer ? (
                <Box
                  sx={{
                    padding: "14px 16px",
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#555",
                      mb: 0.5,
                    }}
                  >
                    Buyer
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#222",
                    }}
                  >
                    {query.answer}
                  </Typography>

                  {query.updatedDate && (
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "#888",
                        mt: 0.8,
                      }}
                    >
                      {new Date(query.updatedDate).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              ) : (
                /* Buyer can answer unanswered query */
                isBuyer && (
                  <Box
                    sx={{
                      padding: "14px 16px",
                      borderTop: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      Your Answer
                    </Typography>

                    <textarea
                      rows={3}
                      value={answers[query.questionId] || ""}
                      onChange={(e) => {
                        setAnswers((prev) => ({
                          ...prev,
                          [query.questionId]: e.target.value,
                        }));
                        setError("");
                      }}
                      placeholder="Enter your answer..."
                      style={{
                        width: "100%",
                        resize: "vertical",
                        padding: "10px",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                      }}
                    >
                      <Button
                        className="btn-contained"
                        onClick={() => handleAnswerQuery(query.questionId)}
                        disabled={answerLoading[query.questionId]}
                      >
                        {answerLoading[query.questionId]
                          ? "Submitting..."
                          : "Submit Answer"}
                      </Button>
                    </Box>
                  </Box>
                )
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Finance Processor raises a new query */}
      {isFinanceProcessor && (
        <Box
          sx={{
            marginTop: "16px",
            padding: "16px",
            border: "1px solid #e0e0e0",
            borderRadius: "10px",
            backgroundColor: "#fafafa",
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              mb: 1,
            }}
          >
            Ask Buyer
          </Typography>

          <textarea
            rows={3}
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
              setError("");
            }}
            placeholder="Enter your question for the buyer..."
            style={{
              width: "100%",
              resize: "vertical",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 1,
            }}
          >
            <Button
              className="btn-contained"
              onClick={handleRaiseQuery}
              disabled={loading}
            >
              {loading ? "Sending..." : "Raise Query"}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FinanceQuerySection;
